/**
 * Mission + delegation observability surfaces — different nouns, one trace
 * tree:
 *
 *  - {@link MissionActivityLane}: the collapsed sub-rows under a mission step
 *    (what the step's agent is actually doing), expanding to a compact web
 *    waterfall rendered from the `/trace` converters.
 *  - {@link AgentActivityPanel}: the standalone cross-context surface — every
 *    delegation a workspace ran, regardless of which mission (if any) spawned
 *    it — behind a `fetchActivity` data port with cursor + refresh.
 *  - {@link FlowWaterfall}: the web counterpart of `/trace`'s ASCII
 *    `renderWaterfall` (which stays CLI) — proportional bars over a FlowTrace.
 *
 * Same styling contract as the rest of `/web-react`: Tailwind classes against
 * the shared design tokens, inline SVG glyphs, no icon library. The pure
 * layout/merge/format helpers are exported for tests and reuse.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react'

import type { StepAgentActivity } from '../missions/agent-activity'
import type { FlowTrace } from '../trace/index'
import { stepActivityFlowTrace } from '../trace/mission-flow'
import { useArrivalStyle } from './motion'

// ── pure helpers ──────────────────────────────────────────────────────────

export type ActivityTone = 'live' | 'ok' | 'error' | 'neutral'

const LIVE_STATUSES = new Set(['pending', 'running'])
const OK_STATUSES = new Set(['completed', 'done', 'succeeded'])
const ERROR_STATUSES = new Set(['failed', 'error', 'cancelled', 'aborted'])

/** Map a delegation status (free-form string on the wire) to a render tone. */
export function activityTone(status: string): ActivityTone {
  const s = status.toLowerCase()
  if (LIVE_STATUSES.has(s)) return 'live'
  if (OK_STATUSES.has(s)) return 'ok'
  if (ERROR_STATUSES.has(s)) return 'error'
  return 'neutral'
}

/** "$0.4000" under a cent shows 4 decimals; null when unknown/zero. */
export function formatActivityCost(costUsd?: number): string | null {
  if (costUsd === undefined || !isFinite(costUsd) || costUsd <= 0) return null
  return costUsd < 0.01 ? `$${costUsd.toFixed(4)}` : `$${costUsd.toFixed(2)}`
}

/** "8s" / "2m 05s" / "1h 12m"; null when unknown. */
export function formatActivityDuration(durationMs?: number): string | null {
  if (durationMs === undefined || !isFinite(durationMs) || durationMs < 0) return null
  const totalSeconds = Math.round(durationMs / 1000)
  if (totalSeconds < 60) return `${totalSeconds}s`
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  if (minutes < 60) return `${minutes}m ${String(seconds).padStart(2, '0')}s`
  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`
}

/** A delegation record on the cross-context surface; `missionRef` links a
 *  promoted delegation back to the mission/step that spawned it. */
export interface AgentActivityRecord extends StepAgentActivity {
  missionRef?: { missionId: string; stepId?: string; label?: string }
}

export interface AgentActivityPage {
  items: AgentActivityRecord[]
  /** Opaque continuation token; absent ⇒ no further pages. */
  nextCursor?: string
}

/**
 * Fold a fetched page into the held rows: dedupe by `taskId` with the
 * incoming row winning (a refresh re-fetches the head page, so newer
 * snapshots of in-flight runs replace stale ones), newest `startedAt` first.
 */
export function mergeActivityPages(
  existing: AgentActivityRecord[],
  incoming: AgentActivityRecord[],
): AgentActivityRecord[] {
  const byTask = new Map<string, AgentActivityRecord>()
  for (const row of existing) byTask.set(row.taskId, row)
  for (const row of incoming) byTask.set(row.taskId, row)
  return [...byTask.values()].sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt))
}

export interface WaterfallRow {
  name: string
  kind: 'pipeline' | 'model' | 'tool'
  /** Bar geometry as percentages of the trace's total span. */
  offsetPct: number
  widthPct: number
  durationLabel: string
  approx: boolean
  /** False only when the span's meta carries an explicit failure. */
  ok: boolean
}

/** Project a FlowTrace into proportional bar geometry for {@link FlowWaterfall}. */
export function waterfallLayout(trace: FlowTrace): WaterfallRow[] {
  const total = trace.totalMs > 0 ? trace.totalMs : 1
  return [...trace.spans]
    .sort((a, b) => a.startMs - b.startMs)
    .map((span) => {
      const meta = span.meta ?? {}
      const failed =
        meta.ok === false || (typeof meta.status === 'string' && activityTone(meta.status) === 'error')
      return {
        name: span.name,
        kind: span.kind,
        offsetPct: Math.max(0, Math.min(100, (span.startMs / total) * 100)),
        widthPct: Math.max(0.5, Math.min(100, ((span.endMs - span.startMs) / total) * 100)),
        durationLabel: `${((span.endMs - span.startMs) / 1000).toFixed(1)}s${span.approx ? '~' : ''}`,
        approx: span.approx === true,
        ok: !failed,
      }
    })
}

// ── glyphs ────────────────────────────────────────────────────────────────

function ChevronGlyph({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="m6 9 6 6 6-6" />
    </svg>
  )
}

function RefreshGlyph({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 12a9 9 0 1 1-2.64-6.36M21 3v6h-6" />
    </svg>
  )
}

function CopyGlyph({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  )
}

/** Copy a trace id to the clipboard — the drill-in's actionable handle instead
 *  of a bare, dead-end string. Falls back silently when the Clipboard API is
 *  unavailable (insecure context / older browser). */
function TraceIdCopy({ traceId }: { traceId: string }) {
  const [copied, setCopied] = useState(false)
  const copy = useCallback(() => {
    void navigator.clipboard?.writeText(traceId).then(
      () => {
        setCopied(true)
        setTimeout(() => setCopied(false), 1200)
      },
      () => {},
    )
  }, [traceId])
  return (
    <button
      type="button"
      onClick={copy}
      title="Copy trace id"
      aria-label="Copy trace id"
      className="inline-flex min-w-0 items-center gap-1.5 rounded text-left font-mono text-muted-foreground transition hover:text-foreground"
    >
      <span className="truncate">{traceId}</span>
      <CopyGlyph className="h-3 w-3 shrink-0" />
      {copied && <span className="shrink-0 not-italic text-success">copied</span>}
    </button>
  )
}

/**
 * The tone dot. A live run's dot no longer pulses: `animate-pulse` is the same
 * 2s fade a loading skeleton uses, so one animation was carrying two unrelated
 * meanings ("no data yet" and "work in progress") and a reader could not learn
 * either. The dot keeps the tone — hue plus the screen-reader word — and the
 * "in flight" signal moves to the one thing on the row that can say it in the
 * shipped vocabulary: the run's own label, sweeping (see {@link RunLabel}).
 */
function StatusDot({ tone }: { tone: ActivityTone }) {
  return (
    <span className="inline-flex items-center">
      <span
        aria-hidden
        className={`h-2 w-2 shrink-0 rounded-full ${
          tone === 'live' ? 'bg-warning' : tone === 'ok' ? 'bg-success' : tone === 'error' ? 'bg-destructive' : 'bg-muted-foreground/40'
        }`}
      />
      <span className="sr-only">{tone}</span>
    </span>
  )
}

/**
 * `tool — detail` for one run. While the run is live the tool name sweeps: the
 * same cue the chat surface's waiting label uses, and the only signal on the
 * row separating an agent that is working from one that is stuck, which is why
 * it declares `data-motion="essential"`. A settled run is plain text — nothing
 * is in flight, so nothing moves.
 *
 * `essential` exempts the label from the blanket reduced-motion collapse; it
 * does NOT keep the sweep running at a reader who asked for less motion. Under
 * `prefers-reduced-motion` tokens.css stops the animation and leaves a dotted
 * rule under the live tool name that its settled siblings on this lane do not
 * carry. That rule is the WHOLE distinction: measured in Chromium, a live and a
 * settled tool name on this lane compute the same color and the same weight
 * (rgb(12,12,21) light / rgb(236,236,241) dark, 500) and differ only in
 * `text-decoration`. The distinction survives; only the movement goes.
 */
function RunLabel({ tool, detail, live }: { tool: string; detail: string; live: boolean }) {
  return (
    <span className="min-w-0 flex-1 truncate">
      <span className={live ? 'agent-shimmer font-medium' : 'font-medium'} data-motion={live ? 'essential' : undefined}>
        {tool}
      </span>
      <span className="text-muted-foreground"> — {detail}</span>
    </span>
  )
}

// ── FlowWaterfall ─────────────────────────────────────────────────────────

const BAR_CLASS: Record<WaterfallRow['kind'], string> = {
  pipeline: 'bg-muted-foreground/30',
  model: 'bg-primary/60',
  tool: 'bg-primary',
}

export interface FlowWaterfallProps {
  trace: FlowTrace
}

/** Compact proportional waterfall over a FlowTrace — span name, bar, duration
 *  per row; total + cost in the footer. */
export function FlowWaterfall({ trace }: FlowWaterfallProps) {
  const rows = waterfallLayout(trace)
  if (rows.length === 0) return null
  const cost = formatActivityCost(trace.costUsd)
  return (
    <div className="space-y-1">
      {rows.map((row, i) => (
        <div key={i} className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)_auto] items-center gap-2">
          <span className="truncate font-mono text-xs text-muted-foreground" title={row.name}>
            {row.name}
          </span>
          <div className="relative h-2 rounded-sm bg-secondary">
            <div
              className={`absolute inset-y-0 rounded-sm ${row.ok ? BAR_CLASS[row.kind] : 'bg-destructive/80'} ${row.approx ? 'opacity-70' : ''}`}
              style={{ left: `${row.offsetPct}%`, width: `${row.widthPct}%` }}
            />
          </div>
          <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground/70">{row.durationLabel}</span>
        </div>
      ))}
      <p className="pt-0.5 text-right font-mono text-xs tabular-nums text-muted-foreground/60">
        {(trace.totalMs / 1000).toFixed(1)}s{cost ? ` · ${cost}` : ''}
      </p>
    </div>
  )
}

// ── MissionActivityLane ───────────────────────────────────────────────────

export interface MissionActivityLaneProps {
  /** The step's delegated-run snapshot (`MissionStepState.agentActivity`). */
  activity: StepAgentActivity[]
  /** Epoch ms origin for the expanded waterfall — usually the step start. */
  startedAt?: number
  /** Wall clock for extending in-flight runs on the waterfall. */
  nowMs?: number
}

/**
 * One delegated run in the lane. A component rather than a `.map` body so the
 * arrival can be frozen at mount — a row that is already on screen and merely
 * changes status must not re-animate, and a stagger index recomputed from a
 * live array is exactly how that happens.
 */
function LaneRow({ run, staggerIndex }: { run: StepAgentActivity; staggerIndex: number }) {
  const arrival = useArrivalStyle(staggerIndex)
  const tone = activityTone(run.status)
  const cost = formatActivityCost(run.costUsd)
  const duration = formatActivityDuration(run.durationMs)
  return (
    <div className="agent-arrive flex items-center gap-2 py-1 text-xs" style={arrival}>
      <StatusDot tone={tone} />
      <RunLabel tool={run.tool} detail={run.detail} live={tone === 'live'} />
      {tone === 'live' && (run.iteration !== undefined || run.phase !== undefined) && (
        <span className="shrink-0 rounded-full bg-warning/10 px-1.5 py-0.5 font-mono text-xs text-warning">
          {[run.iteration !== undefined ? `iter ${run.iteration}` : null, run.phase ?? null]
            .filter(Boolean)
            .join(' · ')}
        </span>
      )}
      <span className="flex shrink-0 items-center gap-1.5 font-mono text-xs tabular-nums text-muted-foreground/70">
        {tone !== 'live' && tone !== 'ok' && <span>{run.status}</span>}
        {cost && <span>{cost}</span>}
        {duration && <span>{duration}</span>}
      </span>
    </div>
  )
}

/**
 * Collapsed sub-rows under a mission step — one row per delegated run —
 * expanding to the step's waterfall. Renders nothing for an empty lane.
 *
 * A sub-row appears because a delegated run STARTED or FINISHED, which is the
 * one kind of list change worth choreographing: it arrives, and the group
 * arrives as a sequence. Keying on `taskId` is what keeps the rest still — the
 * snapshot re-renders every poll, and a row whose status merely advanced holds
 * the DOM node it already had.
 */
export function MissionActivityLane({ activity, startedAt, nowMs }: MissionActivityLaneProps) {
  const [expanded, setExpanded] = useState(false)
  if (activity.length === 0) return null

  return (
    <div className="mt-1 border-l border-border pl-3">
      {activity.map((run, index) => (
        <LaneRow key={run.taskId} run={run} staggerIndex={index} />
      ))}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-1 py-0.5 text-xs font-medium text-muted-foreground/70 transition hover:text-foreground"
      >
        <ChevronGlyph className={`h-3 w-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        timeline
      </button>
      {expanded && (
        <div className="rounded-md border border-border bg-secondary p-2">
          <FlowWaterfall
            trace={stepActivityFlowTrace(activity, {
              ...(startedAt !== undefined ? { startedAt } : {}),
              ...(nowMs !== undefined ? { nowMs } : {}),
            })}
          />
        </div>
      )}
    </div>
  )
}

// ── AgentActivityPanel ────────────────────────────────────────────────────

export interface AgentActivityPanelProps {
  /** Data port — page through the product's delegation records. Called with
   *  no cursor on mount/refresh, with `nextCursor` for older pages. */
  fetchActivity: (cursor?: string) => Promise<AgentActivityPage>
  /** Render the mission link for a promoted delegation (chip, anchor, router
   *  Link — the product's routing, not ours). */
  renderMissionRef?: (ref: NonNullable<AgentActivityRecord['missionRef']>, record: AgentActivityRecord) => ReactNode
  title?: string
  emptyLabel?: string
}

function ActivityRow({
  record,
  renderMissionRef,
  staggerIndex,
}: {
  record: AgentActivityRecord
  renderMissionRef?: AgentActivityPanelProps['renderMissionRef']
  /** Position in the page as it was FIRST rendered — see `useArrivalStyle`. */
  staggerIndex: number
}) {
  const arrival = useArrivalStyle(staggerIndex)
  const [open, setOpen] = useState(false)
  const tone = activityTone(record.status)
  const cost = formatActivityCost(record.costUsd)
  const duration = formatActivityDuration(record.durationMs)

  return (
    <div className="agent-arrive rounded-lg border border-card-edge bg-card" style={arrival}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm">
        <StatusDot tone={tone} />
        <RunLabel tool={record.tool} detail={record.detail} live={tone === 'live'} />
        {tone === 'live' && (record.iteration !== undefined || record.phase !== undefined) && (
          <span className="shrink-0 rounded-full bg-warning/10 px-2 py-0.5 font-mono text-xs text-warning">
            {[record.iteration !== undefined ? `iter ${record.iteration}` : null, record.phase ?? null]
              .filter(Boolean)
              .join(' · ')}
          </span>
        )}
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${
            tone === 'ok'
              ? 'bg-success/10 text-success'
              : tone === 'error'
                ? 'bg-destructive/10 text-destructive'
                : tone === 'live'
                  ? 'bg-warning/10 text-warning'
                  : 'bg-secondary text-muted-foreground'
          }`}
        >
          {record.status}
        </span>
        {cost && <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">{cost}</span>}
        <ChevronGlyph className={`h-3 w-3 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="space-y-2.5 border-t border-border px-3 py-2.5">
          {record.durationMs !== undefined && (
            <div className="rounded-md border border-border bg-secondary p-2">
              <FlowWaterfall trace={stepActivityFlowTrace([record])} />
            </div>
          )}
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 font-mono text-xs">
            <dt className="text-muted-foreground/60">task</dt>
            <dd className="truncate text-muted-foreground">{record.taskId}</dd>
            <dt className="text-muted-foreground/60">started</dt>
            <dd className="text-muted-foreground">{new Date(record.startedAt).toLocaleString()}</dd>
            {duration && (
              <>
                <dt className="text-muted-foreground/60">duration</dt>
                <dd className="text-muted-foreground">{duration}</dd>
              </>
            )}
            {record.traceId && (
              <>
                <dt className="text-muted-foreground/60">trace</dt>
                <dd className="min-w-0">
                  <TraceIdCopy traceId={record.traceId} />
                </dd>
              </>
            )}
          </dl>
          {record.missionRef && renderMissionRef?.(record.missionRef, record)}
        </div>
      )}
    </div>
  )
}

/**
 * The standalone cross-context delegation surface: every agent run the
 * product journaled, mission-spawned or not, with status, cost, drill-in, and
 * a mission link slot for promoted delegations.
 */
/**
 * `loading`/`error` collapsed onto one status rather than two independent
 * booleans, so "loading and errored at once" is unrepresentable instead of
 * merely avoided by careful set-ordering — the same discipline
 * `web-react/async`'s `AsyncResourceState` enforces for a single fetch.
 * `rows`/`cursor` stay separate state because they ACCUMULATE across pages,
 * which a single-resolution status can't express — this is a cursor-paged
 * panel (`useSessionHistory`'s shape), not a one-shot resource.
 */
type AgentActivityStatus = 'loading' | 'error' | 'ready'

export function AgentActivityPanel({ fetchActivity, renderMissionRef, title = 'Agent activity', emptyLabel = 'No agent runs yet.' }: AgentActivityPanelProps) {
  const [rows, setRows] = useState<AgentActivityRecord[]>([])
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [status, setStatus] = useState<AgentActivityStatus>('loading')
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(
    async (from?: string) => {
      setStatus('loading')
      setError(null)
      try {
        const page = await fetchActivity(from)
        setRows((prev) => mergeActivityPages(from === undefined ? [] : prev, page.items))
        setCursor(page.nextCursor)
        setStatus('ready')
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e))
        setStatus('error')
      }
    },
    [fetchActivity],
  )

  useEffect(() => {
    void load()
  }, [load])

  const loading = status === 'loading'

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <h2 className="flex-1 text-sm font-semibold">{title}</h2>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          aria-label="Refresh"
          className="rounded-md p-1.5 text-muted-foreground transition hover:bg-accent hover:text-foreground disabled:opacity-50"
        >
          <RefreshGlyph className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      {status === 'error' && (
        <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          {error}
        </p>
      )}
      {status === 'ready' && rows.length === 0 && <p className="px-1 text-sm text-muted-foreground">{emptyLabel}</p>}
      {/* While `loading` the list is empty AND the empty copy is suppressed, so
          without this the panel is silent to a screen reader from first paint
          until rows land. The region stays MOUNTED and its text changes, because
          a live region that is inserted already carrying its message is not
          reliably announced — and emptying it on arrival is what reports the
          wait ending. */}
      <span role="status" aria-live="polite" aria-busy={loading} className="sr-only">
        {loading ? 'Loading activity…' : ''}
      </span>
      <div className="space-y-1.5" aria-busy={loading}>
        {rows.map((record, index) => (
          <ActivityRow key={record.taskId} record={record} renderMissionRef={renderMissionRef} staggerIndex={index} />
        ))}
      </div>
      {cursor && (
        <button
          type="button"
          onClick={() => void load(cursor)}
          disabled={loading}
          className="w-full rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground transition hover:bg-accent disabled:opacity-50"
        >
          Older runs
        </button>
      )}
    </div>
  )
}
